import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { invalidateResourceAccessQueries } from '@/lib/queryInvalidation';

export type ResourceAccessType = 'prompt' | 'agent';
export type ResourceAccessRole = 'viewer' | 'editor';

export interface ResourcePermissionWithProfile {
  id: string;
  resource_id: string;
  user_id: string;
  role: ResourceAccessRole;
  profile: {
    id: string;
    nome: string | null;
    email: string;
    avatar_url: string | null;
  };
}

const config = {
  prompt: { table: 'prompt_permissions', resourceColumn: 'prompt_id', queryKey: 'prompt-permissions' },
  agent: { table: 'agent_permissions', resourceColumn: 'agent_id', queryKey: 'agent-permissions' },
} as const;

export const useResourcePermissionsList = (type: ResourceAccessType, resourceId?: string) => {
  const { user } = useAuth();
  const cfg = config[type];

  return useQuery({
    queryKey: [cfg.queryKey, resourceId],
    enabled: !!user && !!resourceId,
    queryFn: async (): Promise<ResourcePermissionWithProfile[]> => {
      const { data, error } = await (supabase as any)
        .from(cfg.table)
        .select(`id, ${cfg.resourceColumn}, user_id, role, profiles(id, nome, email, avatar_url)`)
        .eq(cfg.resourceColumn, resourceId!);
      if (error) throw error;

      return (data ?? []).map((row: any) => ({
        id: row.id,
        resource_id: row[cfg.resourceColumn],
        user_id: row.user_id,
        role: row.role,
        profile: row.profiles,
      }));
    },
  });
};

export const useAddResourcePermission = (type: ResourceAccessType) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const cfg = config[type];

  return useMutation({
    mutationFn: async ({ resourceId, userId, role }: { resourceId: string; userId: string; role: ResourceAccessRole }) => {
      const { data, error } = await (supabase as any)
        .from(cfg.table)
        .insert({ [cfg.resourceColumn]: resourceId, user_id: userId, role, granted_by: user!.id } as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      invalidateResourceAccessQueries(queryClient, type, vars.resourceId);
    },
  });
};

export const useUpdateResourcePermission = (type: ResourceAccessType) => {
  const queryClient = useQueryClient();
  const cfg = config[type];

  return useMutation({
    mutationFn: async ({ permissionId, resourceId, role }: { permissionId: string; resourceId: string; role: ResourceAccessRole }) => {
      const { data, error } = await (supabase as any)
        .from(cfg.table)
        .update({ role } as any)
        .eq('id', permissionId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      invalidateResourceAccessQueries(queryClient, type, vars.resourceId);
    },
  });
};

export const useRemoveResourcePermission = (type: ResourceAccessType) => {
  const queryClient = useQueryClient();
  const cfg = config[type];

  return useMutation({
    mutationFn: async ({ permissionId }: { permissionId: string; resourceId: string }) => {
      const { error } = await (supabase as any).from(cfg.table).delete().eq('id', permissionId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      invalidateResourceAccessQueries(queryClient, type, vars.resourceId);
    },
  });
};